import "@/styles/Layout.scss";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import getConstants from "@/scripts/getConstants";
import getAllRequests from "@/scripts/getAllRequests";
import convertTimestamp from "@/scripts/convertTimestamp";
import { handleRequest, archiveRequest } from "@/scripts/manageRequest";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  CheckCircledIcon,
  CrossCircledIcon,
  ListBulletIcon,
  DotsHorizontalIcon,
  ArchiveIcon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Peak from "@/components/Peak";

function ManageProducts() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  // Filters
  const [showFilter, setShowFilter] = useState(true);
  const defaultFilters = {
    startDate: "",
    endDate: "",
    grade: "all",
    form: "all",
    type: "all",
    status: ["escalated", "under review"], // Default statuses
    company: "", // New company filter
  };
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("manage_product_filters");
    return saved ? JSON.parse(saved) : defaultFilters;
  });
  const [filteredList, setFilteredList] = useState([]);

  // Define columns for the table
  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={table.getIsAllRowsSelected()}
          onChange={(e) => {
            if (e.target.checked) {
              table.toggleAllRowsSelected(true);
              setSelectedItems(filteredList.map((item) => item.request_id));
            } else {
              table.toggleAllRowsSelected(false);
              setSelectedItems([]);
            }
          }}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={row.getIsSelected()}
          onChange={(e) => {
            row.toggleSelected(e.target.checked);
            if (e.target.checked) {
              setSelectedItems((prev) => [...prev, row.original.request_id]);
            } else {
              setSelectedItems((prev) =>
                prev.filter((id) => id !== row.original.request_id)
              );
            }
          }}
        />
      ),
    },
    {
      accessorKey: "reference_id",
      header: "Reference ID",
    },
    {
      accessorKey: "company",
      header: "Company",
    },
    {
      accessorKey: "name",
      header: "Person",
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: (info) => convertTimestamp(info.getValue()),
    },
    {
      accessorKey: "product.grade",
      header: "Grade",
    },
    {
      accessorKey: "product.form",
      header: "Form",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => (
        <div className={`status ${info.getValue()}`}>
          {capitalizedStatus(info.getValue())}
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-6">
          <Button
            className="px-6 rounded-full"
            onClick={() => viewProduct(info.row.original)}
          >
            View
          </Button>
          
          <Peak 
            refId={info.row.original.reference_id}
            allImages={info.row.original.product.images} 
          />
          
          <DropdownMenu>
            <DropdownMenuTrigger className="h-9 px-6 py-2 rounded-full bg-black text-white">
              <DotsHorizontalIcon className="h-6 w-6" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="flex flex-col w-[200px] p-4 gap-3">
              {info.row.original.status !== "approved" && (
                <Button
                  className="bg-green"
                  onClick={() => updateStatus(info.row.original, "approved")}
                >
                  Approve
                </Button>
              )}
              {info.row.original.status !== "rejected" && (
                <Button
                  className="bg-[#e03939]"
                  onClick={() => updateStatus(info.row.original, "rejected")}
                >
                  Reject
                </Button>
              )}
              {info.row.original.status !== "escalated" && (
                <Button
                  className="bg-[#ff9720]"
                  onClick={() => updateStatus(info.row.original, "escalated")}
                >
                  Escalate
                </Button>
              )}
              <Button onClick={() => handleArchive(info.row.original)}>
                Archive
              </Button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  // Initialize the table
  const table = useReactTable({
    columns,
    data: filteredList,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10, // Default page size
      },
    },
  });

  const viewProduct = (item) => {
    navigate("/product", { state: { data: item } });
  };

  const capitalizedStatus = (status) => {
    if (status === "processing") {
      return "Under Review";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value, // Only 'status' should be an array
    }));
  };

  // Filter logic
  const filterProducts = (listToFilter = list) => {
    const filtered = listToFilter.filter((product) => {
      // Convert Firestore Timestamp to JavaScript Date
      const productDate = new Date(product.created_at);

      // Parse startDate and endDate
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      const matchDate =
        (!startDate || productDate >= startDate) &&
        (!endDate ||
          productDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999)));

      const matchGrade =
        filters.grade && filters.grade !== "all"
          ? product.product.grade === filters.grade
          : true;

      const matchForm =
        filters.form && filters.form !== "all"
          ? product.product.form === filters.form
          : true;

      const matchType =
        filters.type && filters.type !== "all"
          ? product.type === filters.type
          : true;

      // Check if the product's status is included in the filters.status array
      const matchStatus =
        filters.status.includes("all") ||
        filters.status.includes(product.status) ||
        (filters.status.includes("under review") &&
          product.status === "processing"); // Handle "under review" as "processing"

      // Check if the product's company matches the filter (case-insensitive)
      const matchCompany =
        !filters.company ||
        (product.company &&
          String(product.company)
            .toLowerCase()
            .includes(String(filters.company).toLowerCase()));

      return (
        matchType &&
        matchDate &&
        matchGrade &&
        matchForm &&
        matchStatus &&
        matchCompany
      );
    });

    setFilteredList(filtered); // Update the filteredList state
  };

  const getData = async () => {
    try {
      setLoading(true);
      const fetchedList = await getAllRequests();
      setList(fetchedList);

      // Apply filters after setting the list
      filterProducts(fetchedList);
    } catch (error) {
      console.log("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (item, status) => {
    try {
      setLoading(true);
      // Determine the collection name
      const collection =
        item.type === "buyer_request" ? "buyer_requests" : "seller_requests";

      // Perform the request handling logic
      await handleRequest(collection, item.request_id, status);
      // Fetch updated data after successful action
      await getData();

      console.log("Status updated successfully.");
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (item) => {
    try {
      setLoading(true);
      // Determine the collection name
      const collection =
        item.type === "buyer_request" ? "buyer_requests" : "seller_requests";

      // Perform the request handling logic
      await archiveRequest(collection, item.request_id);
      // Fetch updated data after successful action
      await getData();

      console.log("Status archived successfully.");
    } catch (error) {
      console.error("Error archiving request:", error);
      alert("Failed to archive request. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    try {
      setLoading(true);
      for (const requestId of selectedItems) {
        const item = list.find((item) => item.request_id === requestId);
        if (item) {
          await updateStatus(item, action);
        }
      }
      setSelectedItems([]); // Clear selection after action
    } catch (error) {
      console.error("Error performing bulk action:", error);
      alert("Failed to perform bulk action. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    try {
      setLoading(true);
      for (const requestId of selectedItems) {
        const item = list.find((item) => item.request_id === requestId);
        if (item) {
          await handleArchive(item);
        }
      }
      setSelectedItems([]); // Clear selection after action
    } catch (error) {
      console.error("Error performing bulk archive:", error);
      alert("Failed to perform bulk archive. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchConstants = async () => {
    try {
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    }
  };

  useEffect(() => {
    getData(); // Fetch data once on page load
    fetchConstants();
  }, []);

  useEffect(() => {
    filterProducts();
    localStorage.setItem("manage_product_filters", JSON.stringify(filters));
  }, [filters]);

  if (loading || !grades || !forms) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper mobileTableWrapper">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-bold text-green ml-0.5">
            {showFilter ? "Product Filter" : "Product Form"}
          </h1>
          <div className="highlight mb-3" />
        </div>

        <Button onClick={() => setShowFilter(!showFilter)}>
          <ListBulletIcon />
          Filters
        </Button>
      </div>

      {
        /* Filters */
        showFilter && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Request Type
                </label>
                <Select
                  onValueChange={(value) => handleFilterChange("type", value)}
                  value={filters.type}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.type) || "Select Type"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Type</SelectItem>
                    <SelectItem value="buyer_request">Buyer Request</SelectItem>
                    <SelectItem value="seller_request">
                      Seller Request
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select
                  onValueChange={(value) => {
                    // Convert the selected value to an array
                    const newStatus = value === "all" ? ["all"] : [value];
                    handleFilterChange("status", newStatus);
                  }}
                  value={filters.status[0]} // Display the first selected status
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {filters.status.includes("all")
                        ? "All Status"
                        : filters.status.join(", ")}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                    <SelectItem value="under review">Under Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Grade Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Grade</label>
                <Select
                  onValueChange={(value) => handleFilterChange("grade", value)}
                  value={filters.grade}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.grade) || "Select Grade"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {/* Map */}
                    {grades.map((grade, i) => (
                      <SelectItem key={i} value={grade}>
                        {grade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Form Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Form</label>
                <Select
                  onValueChange={(value) => handleFilterChange("form", value)}
                  value={filters.form}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.form) || "Select Form"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Forms</SelectItem>
                    {/* Map */}
                    {forms.map((form, i) => (
                      <SelectItem key={i} value={form}>
                        {form}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </div>

              {/* Company Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Company
                </label>
                <Input
                  type="text"
                  placeholder="Search by company"
                  value={filters.company}
                  onChange={(e) =>
                    handleFilterChange("company", e.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <h1 className="text-sm font-bold text-green ml-0.5">
                Product Forms
              </h1>
              <div className="highlight mb-3" />
            </div>
          </>
        )
      }

      <div className="flex gap-2 mb-2">
        {/* Bulk Approve */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to approve the selected items?"
                  )
                ) {
                  handleBulkAction("approved");
                }
              }}
              disabled={selectedItems.length === 0}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600"
            >
              <CheckCircledIcon className="h-4 w-4" />
              <span>Bulk Approve</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Approve selected items</p>
          </TooltipContent>
        </Tooltip>

        {/* Bulk Reject */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to reject the selected items?"
                  )
                ) {
                  handleBulkAction("rejected");
                }
              }}
              disabled={selectedItems.length === 0}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600"
            >
              <CrossCircledIcon className="h-4 w-4" />
              <span>Bulk Reject</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Reject selected items</p>
          </TooltipContent>
        </Tooltip>

        {/* Bulk Escalate */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to escalate the selected items?"
                  )
                ) {
                  handleBulkAction("escalated");
                }
              }}
              disabled={selectedItems.length === 0}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600"
            >
              <ExclamationTriangleIcon className="h-4 w-4" />
              <span>Bulk Escalate</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Escalate selected items</p>
          </TooltipContent>
        </Tooltip>

        {/* Bulk Archive */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to archive the selected items?"
                  )
                ) {
                  handleBulkArchive();
                }
              }}
              disabled={selectedItems.length === 0}
              className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600"
            >
              <ArchiveIcon className="h-4 w-4" />
              <span>Bulk Archive</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Archive selected items</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="w-[80px]">
              <span className="text-sm">
                {table.getState().pagination.pageSize}
              </span>
            </SelectTrigger>
            <SelectContent className="!w-[80px]">
              {[10, 25, 50].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  Show {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default ManageProducts;
